// apps/web/src/components/detail/ExploreTab.tsx
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export interface RelatedLink {
  id: string;
  label: string;
  href: string;
}

export interface Reference {
  url: string;
  label: string;
}

export interface SourceSummary {
  id: string;
  title: string;
}

export interface ExtraSection {
  id: string;
  title: string;
  content: ReactNode;
}

export interface ExploreTabProps {
  /** Prose paragraph (e.g. vuln.description / actor.description). */
  summary?: string;
  /** Page-specific sections rendered after summary, before related (e.g. CVSS Vector, Aliases). */
  extraSections?: ExtraSection[];
  /** Related entity links — pages map their domain shape to this. */
  related?: RelatedLink[];
  /** External URLs — opened in a new tab with rel=noreferrer. */
  references?: Reference[];
  /** Related-articles publishers — pages map from useArticles results. */
  sources?: SourceSummary[];
  /** Bottom-most slot for the existing NotesPanel. */
  notesSlot?: ReactNode;
}

export function ExploreTab({
  summary,
  extraSections,
  related,
  references,
  sources,
  notesSlot,
}: ExploreTabProps) {
  const hasSummary = Boolean(summary && summary.trim().length > 0);
  const hasExtras = Boolean(extraSections && extraSections.length > 0);
  const hasRelated = Boolean(related && related.length > 0);
  const hasReferences = Boolean(references && references.length > 0);
  const hasSources = Boolean(sources && sources.length > 0);
  const hasAnyContent =
    hasSummary || hasExtras || hasRelated || hasReferences || hasSources || Boolean(notesSlot);

  return (
    <div className="flex flex-col gap-6">
      {hasSummary ? (
        <section aria-label="Summary" className="text-sm leading-relaxed text-foreground">
          <p>{summary}</p>
        </section>
      ) : null}

      {hasExtras
        ? extraSections!.map((section) => (
            <section key={section.id} aria-labelledby={`explore-extra-${section.id}`} className="flex flex-col gap-2">
              <h3
                id={`explore-extra-${section.id}`}
                className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {section.title}
              </h3>
              <div className="text-sm text-foreground">{section.content}</div>
            </section>
          ))
        : null}

      {hasRelated ? (
        <section aria-labelledby="explore-related-heading" className="flex flex-col gap-2">
          <h3
            id="explore-related-heading"
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Related
          </h3>
          <ul className="flex flex-wrap gap-2">
            {related!.map((item) => (
              <li key={item.id}>
                <Link
                  to={item.href}
                  className="inline-flex items-center rounded-full border border-border bg-muted px-3 py-1 text-xs text-foreground hover:bg-muted/70"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {hasReferences ? (
        <section aria-labelledby="explore-references-heading" className="flex flex-col gap-2">
          <h3
            id="explore-references-heading"
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
          >
            References
          </h3>
          <ul className="list-disc pl-5 text-sm">
            {references!.map((ref) => (
              <li key={ref.url}>
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  {ref.label}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {hasSources ? (
        <section aria-labelledby="explore-sources-heading" className="flex flex-col gap-2">
          <h3
            id="explore-sources-heading"
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Sources
          </h3>
          <ul className="flex flex-wrap gap-2">
            {sources!.map((source) => (
              <li
                key={source.id}
                className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {source.title}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {notesSlot ? <section aria-label="Notes">{notesSlot}</section> : null}

      {!hasAnyContent ? (
        <section
          aria-label="No details"
          className="text-sm leading-relaxed text-muted-foreground"
        >
          <p>No sourced details are available for this actor yet.</p>
        </section>
      ) : null}
    </div>
  );
}
